import React, { InputHTMLAttributes, useState } from 'react';
import classNames from 'classnames';

import showPasswordIcon from 'assets/images/show_password.svg';

import { VALIDATION_CLASS } from 'utils/constants';

import styles from 'assets/scss/ui/Input.module.scss';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  name: string;
  wrapperClassName?: string;
  rightIcon?: React.ComponentType<any>;
  iconSize?: number;
  onRightIconClick?: () => void;
  rightIconTooltip?: string;
  variant?: 'gray' | 'white';
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  name,
  type = 'text',
  className,
  wrapperClassName,
  rightIcon,
  iconSize = 12,
  variant = 'gray',
  onRightIconClick,
  rightIconTooltip,
  disabled,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;

  return (
    <div
      className={classNames(styles.inputWrapper, wrapperClassName, {
        [VALIDATION_CLASS]: !!error,
      })}
    >
      <div className={styles.inputContainer}>
        {label && (
          <label
            htmlFor={name}
            className={classNames(styles.label, className, {
              [styles.labelError]: error,
            })}
          >
            {label}
          </label>
        )}

        <input
          id={name}
          name={name}
          type={inputType}
          className={classNames(styles.input, className, {
            [styles.inputError]: error,
            [styles.input__gray]: variant === 'gray',
            [styles.input__white]: variant === 'white',
            [styles.input__disabled]: disabled,
          })}
          disabled={disabled}
          {...props}
        />

        {isPassword && (
          <button
            type="button"
            className={styles.togglePassword}
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 4C6 4 2.73 6.11 1 9.5C2.73 12.89 6 15 10 15C14 15 17.27 12.89 19 9.5C17.27 6.11 14 4 10 4ZM10 13C8.34 13 7 11.66 7 10C7 8.34 8.34 7 10 7C11.66 7 13 8.34 13 10C13 11.66 11.66 13 10 13ZM10 8.5C9.17 8.5 8.5 9.17 8.5 10C8.5 10.83 9.17 11.5 10 11.5C10.83 11.5 11.5 10.83 11.5 10C11.5 9.17 10.83 8.5 10 8.5Z"
                  fill="#5F6366"
                />
              </svg>
            ) : (
              <img
                src={showPasswordIcon}
                alt="Show password"
                className={styles.showPasswordIcon}
              />
            )}
          </button>
        )}

        {rightIcon && !isPassword && (
          <div
            className={classNames(styles.rightIcon, {
              [styles.rightIconPointer]: !!onRightIconClick,
            })}
            onClick={onRightIconClick}
            title={rightIconTooltip}
          >
            {React.createElement(rightIcon, {
              className: styles.rightIconComponent,
              style: { width: iconSize, height: iconSize },
            })}
          </div>
        )}
      </div>

      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};
